/** @jsxImportSource @emotion/react */
import React from 'react';
import PlanEditorModeToolbar from './planEditor/PlanEditorModeToolbar';
import PlanEditorWorkspace from './planEditor/PlanEditorWorkspace';
import PlanEditorModals from './planEditor/PlanEditorModals';
import ManagementPanel from './ManagementPanel';
import GreenhouseOptionsPanel from './GreenhouseOptionsPanel';
import { usePlanEditorController } from './planEditor/usePlanEditorController';
import type { PlanEditorProps } from './planEditor/PlanEditor.types';

const PlanEditor: React.FC<PlanEditorProps> = (props) => {
  const ctrl = usePlanEditorController(props);

  const modeToolbar = (
    <PlanEditorModeToolbar
      appMode={ctrl.appMode}
      isBusy={props.isBusy ?? false}
      allowBedSelection={props.allowBedSelection ?? false}
      disablePlanningMode={props.disablePlanningMode ?? false}
      disablePlantingMode={props.disablePlantingMode ?? false}
      disableManagementMode={props.disableManagementMode ?? false}
      planningSelection={ctrl.planningSelection}
      selectionRect={ctrl.selectionRect}
      isEraseMode={ctrl.isEraseMode}
      multiSelectedBeds={ctrl.multiSelectedBeds}
      isBulkPlacementMode={ctrl.isBulkPlacementMode}
      bulkGridDimensions={ctrl.bulkGridDimensions}
      onAppModeChange={ctrl.handleAppModeChange}
      onPlanningAction={ctrl.handlePlanningAction}
      onCancelPlanningSelection={() => ctrl.setPlanningSelection(null)}
      onToggleEraseMode={() => ctrl.setIsEraseMode((prev) => !prev)}
      onOpenBulkPlacementModal={ctrl.bulkPlacementHandlers.handleOpenBulkPlacementModal}
      onCancelBulkPlacement={ctrl.bulkPlacementHandlers.handleCancelBulkPlacement}
      onBatchAction={props.onBatchAction ? () => props.onBatchAction!(Array.from(ctrl.multiSelectedBeds)) : undefined}
    />
  );

  const managementPanel = (
    <>
      <ManagementPanel
        bedInfo={ctrl.panelData.selectedBedInfo}
        multiSelectedBeds={ctrl.panelData.multiSelectedBedsInfo}
        bedAutoWatering={ctrl.selectedBedAutoWatering}
        bedLighting={ctrl.selectedBedLighting}
        canRunBedAction={props.canRunManagementBedAction}
        canRunPlantingAction={props.canRunManagementPlantingAction}
        allowAddPlanting={props.allowManagementAddPlanting}
        onViewBed={ctrl.managementHandlers.handleManagementViewBed}
        onViewPlanting={ctrl.managementHandlers.handleManagementViewPlanting}
        onAddPlanting={ctrl.managementHandlers.handleManagementAddPlanting}
        onBedAction={ctrl.managementHandlers.handleManagementBedAction}
        onPlantingAction={ctrl.managementHandlers.handleManagementPlantingAction}
        onClearSelection={ctrl.managementHandlers.handleClearManagementSelection}
        onPlantingAutoHarvestToggle={props.allowManagementAutomation === false
          ? undefined
          : ctrl.handlePlantingAutoHarvestToggle}
        onBedAutoWateringToggle={props.allowManagementAutomation === false
          ? undefined
          : ctrl.handleBedAutoWateringToggle}
        onBedLightingToggle={props.allowManagementAutomation === false
          ? undefined
          : ctrl.handleBedLightingToggle}
      />
      {ctrl.selectedGreenhouseInfo && (
        <GreenhouseOptionsPanel
          subtype={ctrl.selectedGreenhouseInfo.subtype}
          cellCount={ctrl.selectedGreenhouseInfo.cellCount}
          onDelete={ctrl.handleGreenhouseDelete}
          disabled={props.isBusy}
          heatingEnabled={ctrl.selectedGreenhouseHeating.enabled}
          heatingConfig={ctrl.selectedGreenhouseHeating.config}
          onHeatingToggle={ctrl.handleHeatingToggle}
          onHeatingConfigChange={ctrl.handleHeatingConfigChange}
          options={ctrl.selectedGreenhouseInfo.options}
          onParamChange={ctrl.handleGreenhouseParamChange}
          activeFrom={ctrl.selectedGreenhouseInfo.activeFrom}
          activeTo={ctrl.selectedGreenhouseInfo.activeTo}
          onActiveWindowChange={ctrl.handleGreenhouseActiveWindowChange}
        />
      )}
    </>
  );

  return (
    <>
      <PlanEditorWorkspace
        appMode={ctrl.appMode}
        isPlotViewport={ctrl.isPlotViewport}
        modeToolbar={modeToolbar}
        managementPanel={managementPanel}
        entityHintText={ctrl.panelData.entityHintText}
        entityState={ctrl.entityState}
        allowedBoundingBox={props.allowedBoundingBox}
        cellMap={ctrl.cellMap}
        structuralObjects={props.structuralObjects}
        isSelectedCell={ctrl.workspace.isSelectedCell}
        highlightCells={props.highlightCells}
        entityLookup={ctrl.entityLookup}
        multiSelectedBeds={ctrl.multiSelectedBeds}
        multiSelectedPlots={ctrl.multiSelectedPlots}
        bedsWithPlantings={ctrl.workspace.bedsWithPlantings}
        layerData={props.layerData}
        layerVisibility={props.layerVisibility}
        onCellMouseDown={ctrl.handleMouseDown}
        onCellMouseEnter={ctrl.handleMouseEnter}
        onCellMouseLeave={ctrl.handleMouseLeave}
        onCellMouseUp={ctrl.handleMouseUp}
        onGridMouseLeave={ctrl.handleGridMouseLeave}
        onGridScroll={ctrl.handleScroll}
        gridRef={ctrl.handleContainerRef}
        planningSelectionRect={ctrl.selectionRect}
        planningSelectedCells={ctrl.planningSelection?.selectedCellKeys}
        bulkPlacementPreview={ctrl.workspace.bulkPlacementPreview}
        isEraseMode={ctrl.isEraseMode}
        allowBedSelection={props.allowBedSelection ?? false}
        isTransplantTargetMode={props.managementTransplantMode ?? false}
      />

      <PlanEditorModals
        saveId={props.saveId}
        farmId={props.farmId}
        selectedBedForPlanting={ctrl.selectedBedForPlanting}
        plantingModalVisible={ctrl.plantingModalVisible}
        onClosePlantingModal={ctrl.closePlantingModal}
        onPlantingCreated={props.onPlantingCreated}
        bulkPlacementModalVisible={ctrl.bulkPlacementModalVisible}
        onCloseBulkPlacementModal={ctrl.closeBulkPlacementModal}
        onConfirmBulkPlacement={ctrl.bulkPlacementHandlers.handleBulkPlacementConfirm}
        maxBoundingBox={props.globalGrid.maxBoundingBox || 32}
      />
    </>
  );
};

export default PlanEditor;
